import { ApiErrorSchema, parseContract } from '@aibi/contracts';
import { AppError, ERROR_CODES, err, type ErrorCode, type Result } from '@aibi/shared';
import type { z } from 'zod';

export type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Realiza una peticion HTTP y valida la respuesta contra un contrato.
 * Nunca lanza por errores de red o de formato: devuelve un `Result`.
 *
 * @example
 * const result = await requestJson(fetch, '/api/health', HealthResponseSchema);
 */
export async function requestJson<S extends z.ZodType>(
  fetchFn: FetchFn,
  url: string,
  schema: S,
  init: RequestInit = {},
): Promise<Result<z.output<S>, AppError>> {
  let response: Response;
  try {
    response = await fetchFn(url, { ...init, headers: { Accept: 'application/json' } });
  } catch (cause) {
    return err(new AppError('UNAVAILABLE', 'No se pudo contactar con el servidor.', { cause }));
  }

  if (!response.ok) {
    return err(
      new AppError(
        response.status === 404 ? 'NOT_FOUND' : 'UNAVAILABLE',
        `El servidor respondio con estado HTTP ${response.status}.`,
      ),
    );
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch (cause) {
    return err(new AppError('VALIDATION_ERROR', 'La respuesta no es JSON valido.', { cause }));
  }
  return parseContract(schema, body, 'contrato de respuesta');
}

const MAX_SERVER_MESSAGE = 300;
const MAX_SERVER_DETAILS = 20;
const isErrorCode = (code: string): code is ErrorCode =>
  (ERROR_CODES as readonly string[]).includes(code);

/**
 * Traduce el cuerpo `ApiError` del servidor (codigo, mensaje y detalles por campo); si no
 * lo es, devuelve un error generico.
 */
export async function toServerError(response: Response): Promise<AppError> {
  const fallback = new AppError(
    response.status === 404 ? 'NOT_FOUND' : 'UNAVAILABLE',
    `El servidor respondio con estado HTTP ${String(response.status)}.`,
  );
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return fallback;
  }
  const parsed = ApiErrorSchema.safeParse(body);
  if (!parsed.success) return fallback;
  return new AppError(
    isErrorCode(parsed.data.code) ? parsed.data.code : 'UNAVAILABLE',
    parsed.data.message.slice(0, MAX_SERVER_MESSAGE) || fallback.message,
    {
      details: parsed.data.details.slice(0, MAX_SERVER_DETAILS).map((detail) => ({
        path: detail.path.slice(0, 120),
        message: detail.message.slice(0, MAX_SERVER_MESSAGE),
      })),
    },
  );
}

export type JsonMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/** Cabecera anti-CSRF que el servidor exige en las rutas de administracion y sesion. */
export const CSRF_HEADERS = { 'X-AIBI-Request': '1' } as const;

/**
 * Peticion JSON con cualquier metodo, cookies del mismo origen y cabecera anti-CSRF. Una
 * respuesta 204 se valida como `undefined`. Los errores conservan codigo, mensaje y
 * detalles del servidor.
 *
 * @example
 * const result = await sendJson(fetch, 'PUT', '/api/admin/ai', AiSettingsSchema, { body });
 */
export async function sendJson<S extends z.ZodType>(
  fetchFn: FetchFn,
  method: JsonMethod,
  url: string,
  schema: S,
  init: { readonly body?: unknown; readonly signal?: AbortSignal } = {},
): Promise<Result<z.output<S>, AppError>> {
  const headers: Record<string, string> = { Accept: 'application/json', ...CSRF_HEADERS };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  let response: Response;
  try {
    response = await fetchFn(url, {
      method,
      headers,
      credentials: 'same-origin',
      cache: 'no-store',
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      ...(init.signal === undefined ? {} : { signal: init.signal }),
    });
  } catch (cause) {
    return init.signal?.aborted === true
      ? err(new AppError('CANCELLED', 'Solicitud cancelada.'))
      : err(new AppError('UNAVAILABLE', 'No se pudo contactar con el servidor.', { cause }));
  }

  if (!response.ok) return err(await toServerError(response));
  if (response.status === 204) return parseContract(schema, undefined, 'contrato de respuesta');

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (cause) {
    return err(new AppError('VALIDATION_ERROR', 'La respuesta no es JSON valido.', { cause }));
  }
  return parseContract(schema, payload, 'contrato de respuesta');
}

/** Pista accionable que el servidor adjunta en `details` (`path: 'hint'`). */
export const hintOf = (error: AppError): string | null =>
  error.details.find((detail) => detail.path === 'hint')?.message ?? null;

/** Mensajes de validacion por campo (`details` con `path` distinto de `hint`). */
export const fieldErrorsOf = (error: AppError): Readonly<Record<string, string>> =>
  Object.fromEntries(
    error.details
      .filter((detail) => detail.path !== 'hint' && detail.path !== '')
      .map((detail) => [detail.path, detail.message]),
  );

/**
 * Envia un cuerpo JSON por POST y valida la respuesta contra un contrato. Los errores del
 * servidor conservan su codigo y su mensaje (ya sanitizados por la API); la cancelacion
 * se devuelve como `CANCELLED`.
 *
 * @example
 * const result = await postJson(fetch, '/api/ai/generate', body, AIRelayResponseSchema, { signal });
 */
export async function postJson<S extends z.ZodType>(
  fetchFn: FetchFn,
  url: string,
  body: unknown,
  schema: S,
  init: { readonly signal?: AbortSignal } = {},
): Promise<Result<z.output<S>, AppError>> {
  let response: Response;
  try {
    response = await fetchFn(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(init.signal === undefined ? {} : { signal: init.signal }),
    });
  } catch (cause) {
    return init.signal?.aborted === true
      ? err(new AppError('CANCELLED', 'Solicitud cancelada.'))
      : err(new AppError('UNAVAILABLE', 'No se pudo contactar con el servidor.', { cause }));
  }

  if (!response.ok) return err(await toServerError(response));

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (cause) {
    return init.signal?.aborted === true
      ? err(new AppError('CANCELLED', 'Solicitud cancelada.'))
      : err(new AppError('VALIDATION_ERROR', 'La respuesta no es JSON valido.', { cause }));
  }
  return parseContract(schema, payload, 'contrato de respuesta');
}
